// ElevenLabsChunkRecorder.js
//
// Task #33: temporary, opt-in per-chunk recorder for the raw ElevenLabs
// realtime WebSocket audio stream. The goal is to be able to listen to
// every single chunk this client receives, one-by-one, to confirm where
// an intermittent missing-audio segment near the start of a TTS response
// originates (ElevenLabs itself, WS receive path, queue, decoder, or
// playback scheduler).
//
// This module is intentionally isolated:
//   * No imports.
//   * No global side-effects on import (only the explicit `new` call
//     wires state up).
//   * All methods are no-ops unless `isEnabled()` is true at the time
//     of the call, so leaving the file in the tree but disabled costs
//     nothing.
//
// Enablement is server-controlled: the browser side always tries to
// record and POST chunks. The server's `/api/debug/elevenlabs-chunks`
// route is gated by the `ELEVENLABS_CHUNK_RECORDER_ENABLED=1` env var
// and simply returns 404 when off, so the client POST is a cheap no-op
// in that case. To capture files, set that env var server-side and
// restart the Flask server — nothing to toggle in the browser.
//
// Removal is a four-line job once it's served its purpose:
//   1. Delete this file.
//   2. Delete the import + four hook call-sites in
//      `RealtimeLLMToElevenLabs.js` (search for `_elevenChunkRecorder`).
//   3. Delete the `/api/debug/elevenlabs-chunks` route in `main.py`.

const SAVE_ENDPOINT = "/api/debug/elevenlabs-chunks";

function _bufToBase64(buf) {
  // Browser-safe base64 encode of an ArrayBuffer / TypedArray.
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
    );
  }
  return btoa(bin);
}

export class ElevenLabsChunkRecorder {
  constructor() {
    this._session = null;
    this._counter = 0;
  }

  static isEnabled() {
    // Always enabled on the client. The server env flag
    // `ELEVENLABS_CHUNK_RECORDER_ENABLED` is the actual master switch:
    // when off, the save endpoint returns 404 and nothing is written.
    return true;
  }

  start(meta) {
    // Auto-finalize any previous session that wasn't explicitly stopped
    // so chunks never cross a turn boundary.
    if (this._session) {
      try {
        this.stop({ reason: "implicit-restart" });
      } catch (e) {}
    }
    this._session = {
      meta: { ...(meta || {}), startedAtWallMs: performance.now() },
      chunks: [],
    };
    this._counter = 0;
    try {
      console.log(
        "[ElevenChunkRec] session start",
        this._session.meta,
      );
    } catch (e) {}
  }

  recordChunk(buf, sourceKind) {
    if (!this._session) return;
    if (!buf) return;
    try {
      const ab =
        buf instanceof ArrayBuffer
          ? buf
          : buf.buffer && buf.byteLength !== undefined
            ? buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength)
            : null;
      if (!ab) return;
      const idx = this._counter++;
      this._session.chunks.push({
        idx,
        byteLength: ab.byteLength,
        sourceKind: sourceKind || "unknown",
        wallMs: performance.now(),
        base64: _bufToBase64(ab),
      });
    } catch (e) {
      try {
        console.warn("[ElevenChunkRec] recordChunk failed", e);
      } catch (_) {}
    }
  }

  async stop(opts) {
    const session = this._session;
    this._session = null;
    if (!session) return;
    const reason = (opts && opts.reason) || "stop";
    session.meta.stoppedAtWallMs = performance.now();
    session.meta.reason = reason;
    if (!session.chunks.length) {
      try {
        console.log("[ElevenChunkRec] session end (no chunks)", reason);
      } catch (e) {}
      return;
    }
    const payload = { meta: session.meta, chunks: session.chunks };
    try {
      const res = await fetch(SAVE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.warn(
          "[ElevenChunkRec] save failed",
          res.status,
          await res.text().catch(() => ""),
        );
        return;
      }
      const out = await res.json().catch(() => ({}));
      console.log(
        `[ElevenChunkRec] saved ${session.chunks.length} chunks (${reason}) -> ${out.dir || "?"}`,
      );
    } catch (e) {
      console.warn("[ElevenChunkRec] save error", e);
    }
  }
}
